import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import MindGames from './MindGames';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { incrementGamesPlayed, addTime, incrementBadgesEarned, createGame } from '../../../store/slices/gamesSlice';
import { GameId, GameCategory } from '../../../types/enums';
import { TbChartBubbleFilled, TbKeyboardFilled } from 'react-icons/tb';
import { FaUmbrellaBeach, FaHandLizard, FaGamepad, FaClipboard, FaHourglassEnd } from 'react-icons/fa';
import { FaHandsBubbles } from 'react-icons/fa6';
import { MdWaterDrop, MdColorLens, MdTimer } from 'react-icons/md';
import { GiCardJoker, GiTargetShot, GiFocusedLightning, GiBrokenTablet, GiCrackedAlienSkull, GiJigsawBox } from 'react-icons/gi';
import { SiNeteasecloudmusic } from 'react-icons/si';
import { PiListNumbersFill, PiCloudFogFill, PiTargetBold } from 'react-icons/pi';

interface Game {
  id: GameId;
  name: string;
  emoji: string | React.ReactNode;
  description: string;
  instruction: string;
  color: string;
  duration: string;
}

interface MindGamesData {
  calming: Game[];
  cognitive: Game[];
  frustration: Game[];
  puzzles: Game[];
}

interface GameStats {
  gamesPlayed: number;
  totalTime: number;
  badgesEarned: number;
}

interface Badge {
  id: string;
  name: string;
  emoji: string;
  description: string;
}

interface GamesSectionProps {
  userBadges: Badge[];
  availableBadges: Badge[];
}

const GamesSection: React.FC<GamesSectionProps> = ({
  userBadges,
  availableBadges,
}) => {
  const dispatch = useAppDispatch();
  const { stats: gameStats } = useAppSelector((state) => state.games);
  const [selectedGame, setSelectedGame] = useState<GameId | null>(null);
  const [gameInstructions, setGameInstructions] = useState<string>('');
  const [showGamePage, setShowGamePage] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameTime, setGameTime] = useState(0);
  const [gameScore, setGameScore] = useState(0);
  const [gameCompleted, setGameCompleted] = useState(false);

  // Prevent body scroll when game modal is open
  useEffect(() => {
    if (showGamePage) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showGamePage]);

  // Helper function to get game category
  const getGameCategory = (gameId: GameId): GameCategory => {
    if ([GameId.BREATHING_BUBBLE, GameId.ZEN_GARDEN, GameId.WATER_RIPPLE, GameId.COLOR_FLOW].includes(gameId)) {
      return GameCategory.CALMING;
    }
    if ([GameId.MEMORY_FLIP, GameId.FOCUS_DOT, GameId.REACTION_TEST, GameId.PATTERN_BUILDER].includes(gameId)) {
      return GameCategory.COGNITIVE;
    }
    if ([GameId.BUBBLE_POPPER, GameId.GLASS_SMASH, GameId.STRESS_BALL, GameId.TYPING_RAGE, GameId.WHACK_STRESS].includes(gameId)) {
      return GameCategory.FRUSTRATION;
    }
    return GameCategory.PUZZLES;
  };

  // Reset Tools data - only calm activities
  const mindGames: MindGamesData = {
    calming: [
      {
        id: GameId.BREATHING_BUBBLE,
        name: 'Breathing Bubble',
        emoji: <TbChartBubbleFilled className="text-2xl text-blue-300" />,
        description: 'Expand and shrink the bubble to match your breathing rhythm',
        instruction: 'Follow the bubble\'s rhythm - breathe in as it expands, breathe out as it shrinks. Focus on slow, deep breaths for 2-3 minutes.',
        color: 'from-blue-400 to-cyan-400',
        duration: '2-3 min'
      },
      {
        id: GameId.ZEN_GARDEN,
        name: 'Zen Garden',
        emoji: <FaUmbrellaBeach className="text-2xl text-blue-300" />,
        description: 'Drag to create peaceful patterns in the sand',
        instruction: 'Drag your finger or mouse to rake patterns in the sand. Create flowing, meditative designs. No rules, just peaceful creativity.',
        color: 'from-amber-400 to-yellow-400',
        duration: '2-3 min'
      },
      {
        id: GameId.WATER_RIPPLE,
        name: 'Water Ripple',
        emoji: <MdWaterDrop className="text-2xl text-blue-300" />,
        description: 'Tap to create calming water ripples',
        instruction: 'Tap anywhere on the screen to create gentle ripples. Watch them spread and fade. Focus on the peaceful, flowing motion.',
        color: 'from-teal-400 to-blue-400',
        duration: '2-3 min'
      },
      {
        id: GameId.COLOR_FLOW,
        name: 'Color Flow',
        emoji: <MdColorLens className="text-2xl text-blue-300" />,
        description: 'Match soft gradient colors for focus',
        instruction: 'Match the soft gradient colors by tapping the correct sequence. Focus on the gentle color transitions and stay calm.',
        color: 'from-purple-400 to-pink-400',
        duration: '2-3 min'
      }
    ],
    cognitive: [
      {
        id: GameId.MEMORY_FLIP,
        name: 'Memory Flip',
        emoji: <GiCardJoker className="text-2xl text-green-300" />,
        description: 'Classic card-flip memory matching',
        instruction: 'Flip cards to find matching pairs. Start with 6 cards, focus on remembering positions. Take your time, no rush.',
        color: 'from-indigo-400 to-purple-400',
        duration: '2-3 min'
      },
      {
        id: GameId.FOCUS_DOT,
        name: 'Focus Dot',
        emoji: <GiTargetShot className="text-2xl text-green-300" />,
        description: 'Follow a dot that gradually speeds up',
        instruction: 'Keep your eyes on the moving dot. It will gradually speed up - maintain focus and follow its path. Don\'t look away.',
        color: 'from-green-400 to-emerald-400',
        duration: '2-3 min'
      },
      {
        id: GameId.REACTION_TEST,
        name: 'Reaction Test',
        emoji: <GiFocusedLightning className="text-2xl text-green-300" />,
        description: 'Tap when the screen changes color',
        instruction: 'Wait for the screen to change color, then tap as quickly as possible. Stay alert but relaxed. Focus on the color changes.',
        color: 'from-red-400 to-orange-400',
        duration: '2-3 min'
      },
      {
        id: GameId.PATTERN_BUILDER,
        name: 'Pattern Builder',
        emoji: <SiNeteasecloudmusic className="text-2xl text-green-300" />,
        description: 'Copy sequence like Simon Says',
        instruction: 'Watch the sequence of colors and sounds, then repeat it. Start simple and build complexity. Focus on the pattern.',
        color: 'from-violet-400 to-purple-400',
        duration: '2-3 min'
      }
    ],
    frustration: [], // Removed frustration games - only calm activities
    puzzles: [
      {
        id: GameId.JIGSAW_MINI,
        name: 'Jigsaw Mini',
        emoji: <GiJigsawBox className="text-2xl text-purple-300" />,
        description: '3-5 piece puzzle to calm the brain',
        instruction: 'Drag the puzzle pieces to complete the image. Start with 3 pieces, work your way up. Take your time, enjoy the process.',
        color: 'from-emerald-400 to-teal-400',
        duration: '2-3 min'
      },
      {
        id: GameId.MINIMAL_SUDOKU,
        name: 'Minimal Sudoku',
        emoji: <PiListNumbersFill className="text-2xl text-purple-300" />,
        description: 'Short version number puzzle',
        instruction: 'Fill in the numbers 1-4 in each row, column, and 2x2 box. Start with the easy 4x4 grid. Focus on logic, not speed.',
        color: 'from-blue-400 to-indigo-400',
        duration: '2-3 min'
      },
      {
        id: GameId.WORD_CLOUD,
        name: 'Word Cloud Builder',
        emoji: <PiCloudFogFill className="text-2xl text-purple-300" />,
        description: 'Form positive words from random letters',
        instruction: 'Use the given letters to form positive words. Think of uplifting, encouraging words. Build your own word cloud of positivity.',
        color: 'from-purple-400 to-pink-400',
        duration: '2-3 min'
      }
    ]
  };

  // Game-specific state
  const [bubbleSize, setBubbleSize] = useState(50);
  const [bubbleExpanding, setBubbleExpanding] = useState(true);
  const [ripples, setRipples] = useState<Array<{ id: number, x: number, y: number, size: number }>>([]);
  const [memoryCards, setMemoryCards] = useState<Array<{ id: number, value: string, flipped: boolean, matched: boolean }>>([]);
  const [focusDotPosition, setFocusDotPosition] = useState({ x: 50, y: 50 });
  const [reactionColor, setReactionColor] = useState('bg-blue-500');
  const [patternSequence, setPatternSequence] = useState<string[]>([]);
  const [userPattern, setUserPattern] = useState<string[]>([]);
  const [bubbles, setBubbles] = useState<Array<{ id: number, x: number, y: number, size: number }>>([]);
  const [glassPanels, setGlassPanels] = useState<Array<{ id: number, broken: boolean }>>([]);
  const [stressBallSize, setStressBallSize] = useState(100);
  const [typingWords, setTypingWords] = useState<string[]>([]);
  const [stressMonsters, setStressMonsters] = useState<Array<{ id: number, x: number, y: number, active: boolean }>>([]);
  const [puzzlePieces, setPuzzlePieces] = useState<Array<{ id: number, x: number, y: number, correct: boolean }>>([]);
  const [sudokuGrid, setSudokuGrid] = useState<Array<Array<number>>>([]);
  const [wordLetters, setWordLetters] = useState<string[]>([]);
  const [formedWords, setFormedWords] = useState<string[]>([]);

  // Game timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (gameStarted && !gameCompleted) {
      interval = setInterval(() => {
        setGameTime(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [gameStarted, gameCompleted]);

  // Game functions
  const startGame = () => {
    setGameStarted(true);
    setGameTime(0);
    setGameScore(0);
    setGameCompleted(false);

    // Initialize game-specific data
    if (selectedGame === GameId.BREATHING_BUBBLE) {
      setBubbleSize(50);
      setBubbleExpanding(true);
      // Start breathing animation
      const breathingInterval = setInterval(() => {
        setBubbleExpanding(prev => !prev);
        setBubbleSize(prev => prev === 50 ? 100 : 50);
      }, 3000);
      setTimeout(() => clearInterval(breathingInterval), 180000); // 3 minutes
    } else if (selectedGame === GameId.WATER_RIPPLE) {
      setRipples([]);
    } else if (selectedGame === GameId.MEMORY_FLIP) {
      const cards = Array.from({ length: 8 }, (_, i) => ({
        id: i,
        value: String.fromCharCode(65 + Math.floor(i / 2)),
        flipped: false,
        matched: false
      })).sort(() => Math.random() - 0.5);
      setMemoryCards(cards);
    } else if (selectedGame === GameId.FOCUS_DOT) {
      setFocusDotPosition({ x: 50, y: 50 });
      // Start dot movement
      const dotInterval = setInterval(() => {
        setFocusDotPosition({
          x: Math.random() * 80 + 10,
          y: Math.random() * 80 + 10
        });
      }, 2000);
      setTimeout(() => clearInterval(dotInterval), 180000);
    } else if (selectedGame === GameId.REACTION_TEST) {
      setReactionColor('bg-blue-500');
      // Start color changes
      const colorInterval = setInterval(() => {
        const colors = ['bg-red-500', 'bg-green-500', 'bg-yellow-500', 'bg-purple-500', 'bg-blue-500'];
        setReactionColor(colors[Math.floor(Math.random() * colors.length)]);
      }, 3000);
      setTimeout(() => clearInterval(colorInterval), 180000);
    } else if (selectedGame === GameId.PATTERN_BUILDER) {
      const colors = ['red', 'blue', 'green', 'yellow'];
      const sequence = Array.from({ length: 4 }, () => colors[Math.floor(Math.random() * colors.length)]);
      setPatternSequence(sequence);
      setUserPattern([]);
    } else if (selectedGame === GameId.BUBBLE_POPPER) {
      setBubbles([]);
      // Auto-generate bubbles
      const bubbleInterval = setInterval(() => {
        setBubbles(prev => [...prev, {
          id: Date.now(),
          x: Math.random() * 80 + 10,
          y: Math.random() * 80 + 10,
          size: Math.random() * 30 + 20
        }]);
      }, 1000);
      setTimeout(() => clearInterval(bubbleInterval), 180000);
    } else if (selectedGame === GameId.GLASS_SMASH) {
      const panels = Array.from({ length: 9 }, (_, i) => ({ id: i, broken: false }));
      setGlassPanels(panels);
    } else if (selectedGame === GameId.STRESS_BALL) {
      setStressBallSize(100);
    } else if (selectedGame === GameId.TYPING_RAGE) {
      const words = ['STRESS', 'RELAX', 'FOCUS', 'CALM', 'BREATHE', 'PEACE', 'CHILL', 'ZEN'];
      setTypingWords(words);
    } else if (selectedGame === GameId.WHACK_STRESS) {
      setStressMonsters([]);
      // Generate stress monsters
      const monsterInterval = setInterval(() => {
        setStressMonsters(prev => [...prev, {
          id: Date.now(),
          x: Math.random() * 80 + 10,
          y: Math.random() * 80 + 10,
          active: true
        }]);
      }, 2000);
      setTimeout(() => clearInterval(monsterInterval), 180000);
    } else if (selectedGame === GameId.JIGSAW_MINI) {
      const pieces = Array.from({ length: 4 }, (_, i) => ({
        id: i,
        x: Math.random() * 300,
        y: Math.random() * 200,
        correct: false
      }));
      setPuzzlePieces(pieces);
    } else if (selectedGame === GameId.MINIMAL_SUDOKU) {
      // Create a simple 4x4 sudoku with some pre-filled numbers
      const grid = [
        [1, 0, 3, 0],
        [0, 2, 0, 4],
        [3, 0, 1, 0],
        [0, 4, 0, 2]
      ];
      setSudokuGrid(grid);
    } else if (selectedGame === GameId.WORD_CLOUD) {
      const letters = ['F', 'O', 'C', 'U', 'S', 'C', 'A', 'L', 'M', 'P', 'E', 'A', 'C', 'E'];
      setWordLetters(letters);
      setFormedWords([]);
    }
  };

  const endGame = async () => {
    if (!selectedGame) return;

    // Stop the game immediately
    setGameStarted(false);
    setGameCompleted(false);

    // Get game details
    const game = getGameById(selectedGame);
    const category = getGameCategory(selectedGame);

    if (!game || !category) {
      console.error('Game not found:', selectedGame);
      // Still reset even if game not found
      setGameTime(0);
      setGameScore(0);
      return;
    }

    // Save current game time and score before resetting
    const currentGameTime = gameTime;
    const currentGameScore = gameScore;

    // Reset game state immediately to show "Start Game" button
    setGameTime(0);
    setGameScore(0);

    // Create game record with full game information (async, don't block UI)
    // Convert duration from seconds to minutes
    const durationInMinutes = currentGameTime / 60;
    // Convert emoji to string for API (if it's a ReactNode, use empty string or derive from game)
    const emojiString = typeof game.emoji === 'string' ? game.emoji : '';
    try {
      await dispatch(createGame({
        gameId: selectedGame,
        gameName: game.name,
        category: category,
        emoji: emojiString,
        description: game.description,
        duration: durationInMinutes,
        score: currentGameScore,
        completed: true,
      })).unwrap();
    } catch (error) {
      console.error('Failed to create game record:', error);
    }

    // Update Redux store directly
    dispatch(incrementGamesPlayed());
    dispatch(addTime(currentGameTime));
    dispatch(incrementBadgesEarned());
  };

  const closeGame = () => {
    setShowGamePage(false);
    setSelectedGame(null);
    setGameInstructions('');
    setGameStarted(false);
    setGameCompleted(false);
    setGameTime(0);
    setGameScore(0);
  };

  const handleGameSelect = (gameId: GameId, instruction: string) => {
    setSelectedGame(gameId);
    setGameInstructions(instruction);
    setShowGamePage(true);
  };

  const getGameById = (id: GameId): Game | undefined => {
    return Object.values(mindGames).flat().find(g => g.id === id);
  };

  return (
    <>
      <MindGames
        mindGames={mindGames}
        gameStats={gameStats}
        userBadges={userBadges.map(b => b.id)}
        availableBadges={availableBadges}
        onGameSelect={handleGameSelect}
      />

      {/* Individual Game Page */}
      {showGamePage && selectedGame && createPortal(
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[9999] p-2 sm:p-4">
          <div className="bg-[#FFF8F0] rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 max-w-6xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden border border-gray-200/60 shadow-xl flex flex-col">
            {/* Game Header */}
            <div className="flex justify-between items-start sm:items-center mb-4 sm:mb-6 flex-shrink-0 gap-3">
              <div className="flex-1 min-w-0">
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-1 sm:mb-2 truncate">
                  {getGameById(selectedGame)?.name}
                </h2>
                <p className="text-xs sm:text-sm text-gray-600 truncate">
                  {getGameById(selectedGame)?.description}
                </p>
              </div>
              <button
                onClick={closeGame}
                className="text-gray-400 hover:text-gray-900 text-xl sm:text-2xl font-bold flex-shrink-0"
              >
                ×
              </button>
            </div>

            {/* Game Timer and Score */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 flex-shrink-0 gap-3 sm:gap-0">
              <div className="flex space-x-3 sm:space-x-4">
                <div className="bg-orange-50/50 rounded-lg p-2 sm:p-3 border border-orange-200/50">
                  <div className="text-base sm:text-lg font-bold text-orange-600">{gameTime}s</div>
                  <div className="text-xs text-gray-600">Time</div>
                </div>
                <div className="bg-orange-50/50 rounded-lg p-2 sm:p-3 border border-orange-200/50">
                  <div className="text-base sm:text-lg font-bold text-green-600">{gameScore}</div>
                  <div className="text-xs text-gray-600">Score</div>
                </div>
              </div>
              <div className="flex space-x-2 sm:space-x-3">
                {!gameStarted && !gameCompleted && (
                  <button
                    onClick={startGame}
                    className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-lg sm:rounded-xl font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-orange-200 flex items-center gap-1.5 sm:gap-2 text-sm sm:text-base"
                  >
                    <FaGamepad className="text-lg sm:text-xl" />
                    <span>Start Practice</span>
                  </button>
                )}
                {gameStarted && !gameCompleted && (
                  <button
                    onClick={endGame}
                    className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-lg sm:rounded-xl font-semibold transition-all duration-300 flex items-center gap-1.5 sm:gap-2 text-sm sm:text-base"
                  >
                    <FaHourglassEnd className="text-lg sm:text-xl" />
                    <span>End Practice</span>
                  </button>
                )}
              </div>
            </div>

            {/* Game Instructions */}
            {!gameStarted && !gameCompleted && (
              <div className="bg-gradient-to-r from-orange-50/80 to-orange-100/50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-orange-200/50 mb-4 sm:mb-8 flex-shrink-0">
                <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                  <FaClipboard className="text-lg sm:text-xl" />
                  Instructions
                </h4>
                <div className="text-sm sm:text-base text-gray-700 leading-relaxed mb-3 sm:mb-4">
                  {gameInstructions}
                </div>
                <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                  <div className="bg-orange-100 text-orange-700 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm flex items-center gap-1">
                    <MdTimer className="text-sm sm:text-base" />
                    {getGameById(selectedGame)?.duration}
                  </div>
                  <div className="bg-green-100 text-green-700 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm flex items-center gap-1">
                    <PiTargetBold className="text-sm sm:text-base" />
                    Max 3 minutes
                  </div>
                </div>
              </div>
            )}

            {/* Game Area */}
            <div className="bg-gray-50/50 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 border border-orange-200/50 flex-1 overflow-y-auto min-h-0 pb-4">
              {!gameStarted && !gameCompleted && (
                <div className="text-center">
                  <div className="flex justify-center mb-4">
                    {typeof getGameById(selectedGame)?.emoji === 'string' ? (
                      <span className="text-6xl">{getGameById(selectedGame)?.emoji}</span>
                    ) : (
                      <div className="text-6xl">{getGameById(selectedGame)?.emoji}</div>
                    )}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Ready to Begin?</h3>
                  <p className="text-gray-600 mb-6">
                    Click "Start Practice" to begin your {getGameById(selectedGame)?.name} session.
                  </p>
                </div>
              )}

              {/* Breathing Bubble Game */}
              {gameStarted && selectedGame === GameId.BREATHING_BUBBLE && (
                <div className="text-center">
                  <div className="mb-6">
                    <div className="text-gray-900 text-lg mb-4">Follow the bubble's breathing rhythm</div>
                    <div
                      className="bg-gradient-to-r from-orange-400 to-orange-500 rounded-full mx-auto transition-all duration-3000 ease-in-out"
                      style={{
                        width: `${bubbleSize}px`,
                        height: `${bubbleSize}px`,
                        animation: bubbleExpanding ? 'pulse 3s ease-in-out infinite' : 'none'
                      }}
                    />
                  </div>
                  <div className="text-gray-700 mb-4">
                    {bubbleExpanding ? 'Breathe IN...' : 'Breathe OUT...'}
                  </div>
                  <div className="text-sm text-gray-600">
                    Click the bubble to sync your breathing
                  </div>
                  <button
                    onClick={() => setGameScore(prev => prev + 1)}
                    className="mt-4 px-6 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors"
                  >
                    Sync Breathing
                  </button>
                </div>
              )}

              {/* Water Ripple Game */}
              {gameStarted && selectedGame === GameId.WATER_RIPPLE && (
                <div className="text-center">
                  <div className="text-gray-900 text-lg mb-6">Tap to create calming ripples</div>
                  <div
                    className="relative w-full h-96 bg-gradient-to-b from-orange-200 to-orange-300 rounded-2xl cursor-pointer"
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const x = e.clientX - rect.left;
                      const y = e.clientY - rect.top;
                      const newRipple = {
                        id: Date.now(),
                        x,
                        y,
                        size: 0
                      };
                      setRipples(prev => [...prev, newRipple]);
                      setGameScore(prev => prev + 1);
                    }}
                  >
                    {ripples.map((ripple) => (
                      <div
                        key={ripple.id}
                        className="absolute border-2 border-orange-500/50 rounded-full animate-ping"
                        style={{
                          left: ripple.x - 25,
                          top: ripple.y - 25,
                          width: 50,
                          height: 50
                        }}
                      />
                    ))}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-gray-900 text-xl">Tap anywhere to create ripples</div>
                    </div>
                  </div>
                  <div className="mt-4 text-gray-700">
                    Ripples created: {gameScore}
                  </div>
                </div>
              )}

              {/* Zen Garden Game */}
              {gameStarted && selectedGame === GameId.ZEN_GARDEN && (
                <div className="text-center">
                  <div className="text-gray-900 text-lg mb-6">Create peaceful patterns in the sand</div>
                  <div className="relative w-full h-96 bg-gradient-to-b from-amber-200 to-yellow-300 rounded-2xl">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-amber-800 text-xl">Drag to create patterns</div>
                    </div>
                    <div className="absolute bottom-4 left-4 text-amber-800 text-sm">
                      Use your mouse to draw calming patterns
                    </div>
                  </div>
                  <div className="mt-4 text-gray-700">
                    Patterns created: {gameScore}
                  </div>
                  <button
                    onClick={() => setGameScore(prev => prev + 1)}
                    className="mt-4 px-6 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors"
                  >
                    Create Pattern
                  </button>
                </div>
              )}

              {/* Color Flow Game */}
              {gameStarted && selectedGame === GameId.COLOR_FLOW && (
                <div className="text-center">
                  <div className="text-gray-900 text-lg mb-6">Match the soft gradient colors</div>
                  <div className="grid grid-cols-2 gap-4 max-w-xs mx-auto mb-6">
                    {['red', 'blue', 'green', 'yellow'].map((color) => (
                      <button
                        key={color}
                        onClick={() => setGameScore(prev => prev + 1)}
                        className={`w-20 h-20 rounded-lg bg-${color}-400 hover:bg-${color}-500 transition-all duration-300 hover:scale-110 shadow-lg`}
                      />
                    ))}
                  </div>
                  <div className="text-gray-700">
                    Colors matched: {gameScore}
                  </div>
                </div>
              )}

              {/* Memory Flip Game */}
              {gameStarted && selectedGame === GameId.MEMORY_FLIP && (
                <div className="text-center">
                  <div className="text-gray-900 text-lg mb-6">Find matching pairs!</div>
                  <div className="grid grid-cols-4 gap-4 max-w-md mx-auto">
                    {memoryCards.map((card) => (
                      <button
                        key={card.id}
                        onClick={() => {
                          if (!card.flipped && !card.matched) {
                            const flippedCards = memoryCards.filter(c => c.flipped && !c.matched);

                            if (flippedCards.length === 0) {
                              // First card flip
                              setMemoryCards(prev => prev.map(c =>
                                c.id === card.id ? { ...c, flipped: true } : c
                              ));
                            } else if (flippedCards.length === 1) {
                              // Second card flip
                              setMemoryCards(prev => prev.map(c =>
                                c.id === card.id ? { ...c, flipped: true } : c
                              ));

                              // Check for match after a short delay
                              setTimeout(() => {
                                setMemoryCards(prev => {
                                  const newCards = [...prev];
                                  const flipped = newCards.filter(c => c.flipped && !c.matched);

                                  if (flipped.length === 2 && flipped[0].value === flipped[1].value) {
                                    // Match found
                                    newCards.forEach(c => {
                                      if (c.flipped && !c.matched) {
                                        c.matched = true;
                                        c.flipped = true;
                                      }
                                    });
                                    setGameScore(prev => prev + 10);
                                  } else {
                                    // No match, flip back
                                    newCards.forEach(c => {
                                      if (c.flipped && !c.matched) {
                                        c.flipped = false;
                                      }
                                    });
                                  }
                                  return newCards;
                                });
                              }, 1000);
                            }
                          }
                        }}
                        className={`w-16 h-16 rounded-lg font-bold text-xl transition-all duration-300 ${card.flipped || card.matched
                          ? card.matched ? 'bg-green-500 text-white' : 'bg-orange-500 text-white'
                          : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
                          }`}
                      >
                        {card.flipped || card.matched ? card.value : '?'}
                      </button>
                    ))}
                  </div>
                  <div className="mt-4 text-gray-700">
                    Matched pairs: {memoryCards.filter(c => c.matched).length / 2}
                  </div>
                </div>
              )}

              {/* Focus Dot Game */}
              {gameStarted && selectedGame === GameId.FOCUS_DOT && (
                <div className="text-center">
                  <div className="text-gray-900 text-lg mb-6">Follow the green dot with your eyes</div>
                  <div className="relative w-full h-96 bg-gray-200 rounded-2xl mx-auto">
                    <div
                      className="absolute w-6 h-6 bg-green-500 rounded-full transition-all duration-1000 shadow-lg shadow-green-500/50"
                      style={{
                        left: `${focusDotPosition.x}%`,
                        top: `${focusDotPosition.y}%`
                      }}
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-gray-900 text-lg">Keep your eyes on the moving dot</div>
                    </div>
                  </div>
                  <div className="mt-4 text-gray-700">
                    Focus level: {Math.min(100, gameScore * 10)}%
                  </div>
                  <button
                    onClick={() => setGameScore(prev => prev + 1)}
                    className="mt-4 px-6 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                  >
                    Track Focus
                  </button>
                </div>
              )}

              {/* Reaction Test Game */}
              {gameStarted && selectedGame === GameId.REACTION_TEST && (
                <div className="text-center">
                  <div className="text-gray-900 text-lg mb-6">Tap when the screen changes color!</div>
                  <div
                    className={`w-64 h-64 mx-auto rounded-2xl cursor-pointer transition-all duration-500 ${reactionColor}`}
                    onClick={() => {
                      const colors = ['bg-red-500', 'bg-green-500', 'bg-yellow-500', 'bg-orange-500'];
                      setReactionColor(colors[Math.floor(Math.random() * colors.length)]);
                      setGameScore(prev => prev + 1);
                    }}
                  />
                </div>
              )}

              {/* Pattern Builder Game */}
              {gameStarted && selectedGame === GameId.PATTERN_BUILDER && (
                <div className="text-center">
                  <div className="text-gray-900 text-lg mb-6">Watch the pattern, then repeat it!</div>

                  {/* Show the sequence to memorize */}
                  <div className="mb-6">
                    <div className="text-gray-700 mb-2">Pattern to memorize:</div>
                    <div className="flex justify-center space-x-2">
                      {patternSequence.map((color, index) => (
                        <div
                          key={index}
                          className={`w-12 h-12 rounded-lg bg-${color}-500 border-2 border-gray-300`}
                        />
                      ))}
                    </div>
                  </div>

                  {/* User input buttons */}
                  <div className="grid grid-cols-2 gap-4 mb-6 max-w-xs mx-auto">
                    {['red', 'blue', 'green', 'yellow'].map((color) => (
                      <button
                        key={color}
                        onClick={() => {
                          setUserPattern(prev => [...prev, color]);
                          setGameScore(prev => prev + 1);
                        }}
                        className={`w-16 h-16 rounded-lg bg-${color}-500 hover:bg-${color}-600 transition-all duration-300 shadow-lg`}
                      />
                    ))}
                  </div>

                  <div className="text-gray-700 mb-4">
                    Your pattern: {userPattern.join(' → ')}
                  </div>

                  {/* Check pattern button */}
                  <button
                    onClick={() => {
                      const isCorrect = userPattern.length === patternSequence.length &&
                        userPattern.every((color, index) => color === patternSequence[index]);
                      if (isCorrect) {
                        setGameScore(prev => prev + 20);
                        setUserPattern([]);
                        // Generate new pattern
                        const colors = ['red', 'blue', 'green', 'yellow'];
                        const newSequence = Array.from({ length: 4 }, () => colors[Math.floor(Math.random() * colors.length)]);
                        setPatternSequence(newSequence);
                      } else {
                        setUserPattern([]);
                      }
                    }}
                    className="px-6 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors"
                  >
                    Check Pattern
                  </button>
                </div>
              )}

              {/* Bubble Popper Game */}
              {gameStarted && selectedGame === GameId.BUBBLE_POPPER && (
                <div className="text-center">
                  <div className="text-gray-900 text-lg mb-6">Pop the bubbles to release stress!</div>
                  <div
                    className="relative w-full h-96 bg-gradient-to-b from-orange-200 to-orange-300 rounded-2xl overflow-hidden"
                  >
                    {bubbles.map((bubble) => (
                      <button
                        key={bubble.id}
                        onClick={() => {
                          setBubbles(prev => prev.filter(b => b.id !== bubble.id));
                          setGameScore(prev => prev + 1);
                        }}
                        className="absolute bg-white/60 hover:bg-white/80 rounded-full animate-bounce transition-all duration-300 hover:scale-110"
                        style={{
                          left: bubble.x - bubble.size / 2,
                          top: bubble.y - bubble.size / 2,
                          width: bubble.size,
                          height: bubble.size
                        }}
                      />
                    ))}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-gray-900 text-xl">Pop the bubbles!</div>
                    </div>
                  </div>
                  <div className="mt-4 text-gray-700">
                    Bubbles popped: {gameScore}
                  </div>
                </div>
              )}

              {/* Glass Smash Game */}
              {gameStarted && selectedGame === GameId.GLASS_SMASH && (
                <div className="grid grid-cols-3 gap-4">
                  {glassPanels.map((panel) => (
                    <button
                      key={panel.id}
                      onClick={() => {
                        setGlassPanels(prev => prev.map(p =>
                          p.id === panel.id ? { ...p, broken: true } : p
                        ));
                        setGameScore(prev => prev + 1);
                      }}
                      className={`w-24 h-24 rounded-lg transition-all duration-300 ${panel.broken
                        ? 'bg-red-500/50 border-2 border-red-400'
                        : 'bg-gray-300 hover:bg-gray-200 border-2 border-gray-400'
                        }`}
                    >
                      {panel.broken ? '💥' : '🪟'}
                    </button>
                  ))}
                </div>
              )}

              {/* Stress Ball Game */}
              {gameStarted && selectedGame === GameId.STRESS_BALL && (
                <div className="text-center">
                  <div className="text-gray-900 text-lg mb-6">Squeeze the stress ball!</div>
                  <div
                    className="bg-gradient-to-r from-orange-400 to-red-500 rounded-full mx-auto cursor-pointer transition-all duration-300"
                    style={{
                      width: `${stressBallSize}px`,
                      height: `${stressBallSize}px`
                    }}
                    onMouseDown={() => setStressBallSize(60)}
                    onMouseUp={() => setStressBallSize(100)}
                    onClick={() => setGameScore(prev => prev + 1)}
                  />
                </div>
              )}

              {/* Typing Rage Game */}
              {gameStarted && selectedGame === GameId.TYPING_RAGE && (
                <div className="text-center">
                  <div className="text-gray-900 text-lg mb-6">Type the words as fast as you can!</div>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    {typingWords.map((word, index) => (
                      <div key={index} className="bg-orange-100 rounded-lg p-4 text-gray-900 font-bold text-xl">
                        {word}
                      </div>
                    ))}
                  </div>
                  <input
                    type="text"
                    className="w-full p-4 bg-white border border-orange-200 text-gray-900 rounded-lg text-center text-xl focus:ring-2 focus:ring-orange-400 focus:border-orange-400 focus:outline-none"
                    placeholder="Type here to vent your frustration..."
                    onChange={(e) => {
                      if (typingWords.includes(e.target.value.toUpperCase())) {
                        setGameScore(prev => prev + 1);
                        e.target.value = '';
                      }
                    }}
                  />
                </div>
              )}

              {/* Whack-a-Stress Game */}
              {gameStarted && selectedGame === GameId.WHACK_STRESS && (
                <div className="text-center">
                  <div className="text-gray-900 text-lg mb-6">Whack the stress monsters!</div>
                  <div className="relative w-full h-96 bg-gradient-to-b from-orange-200 to-orange-300 rounded-2xl">
                    {stressMonsters.map((monster) => (
                      <button
                        key={monster.id}
                        onClick={() => {
                          setStressMonsters(prev => prev.filter(m => m.id !== monster.id));
                          setGameScore(prev => prev + 1);
                        }}
                        className="absolute text-4xl animate-bounce hover:scale-110 transition-transform duration-200"
                        style={{
                          left: `${monster.x}%`,
                          top: `${monster.y}%`
                        }}
                      >
                        👾
                      </button>
                    ))}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-gray-900 text-xl">Whack them away!</div>
                    </div>
                  </div>
                  <div className="mt-4 text-gray-700">
                    Monsters whacked: {gameScore}
                  </div>
                </div>
              )}

              {/* Jigsaw Mini Game */}
              {gameStarted && selectedGame === GameId.JIGSAW_MINI && (
                <div className="text-center">
                  <div className="text-gray-900 text-lg mb-6">Click the pieces to complete the puzzle!</div>
                  <div className="grid grid-cols-2 gap-4 max-w-xs mx-auto">
                    {puzzlePieces.map((piece) => (
                      <button
                        key={piece.id}
                        onClick={() => {
                          setPuzzlePieces(prev => prev.map(p =>
                            p.id === piece.id ? { ...p, correct: !p.correct } : p
                          ));
                          setGameScore(prev => prev + 1);
                        }}
                        className={`w-20 h-20 rounded-lg transition-all duration-300 ${piece.correct
                          ? 'bg-green-500 border-2 border-green-300'
                          : 'bg-gradient-to-br from-orange-400 to-orange-500 hover:from-orange-300 hover:to-orange-400'
                          }`}
                      >
                        {piece.correct ? '✓' : '🧩'}
                      </button>
                    ))}
                  </div>
                  <div className="mt-4 text-gray-700">
                    Pieces placed: {puzzlePieces.filter(p => p.correct).length}/4
                  </div>
                </div>
              )}

              {/* Minimal Sudoku Game */}
              {gameStarted && selectedGame === GameId.MINIMAL_SUDOKU && (
                <div className="text-center">
                  <div className="text-gray-900 text-lg mb-6">Fill in the numbers 1-4!</div>
                  <div className="grid grid-cols-4 gap-2 mx-auto w-64">
                    {sudokuGrid.map((row, rowIndex) =>
                      row.map((cell, colIndex) => (
                        <input
                          key={`${rowIndex}-${colIndex}`}
                          type="number"
                          min="1"
                          max="4"
                          className="w-12 h-12 text-center bg-white text-gray-900 rounded border border-orange-200 focus:ring-2 focus:ring-orange-400 focus:border-orange-400 focus:outline-none"
                          value={cell || ''}
                          onChange={(e) => {
                            const newGrid = [...sudokuGrid];
                            newGrid[rowIndex][colIndex] = parseInt(e.target.value) || 0;
                            setSudokuGrid(newGrid);
                            setGameScore(prev => prev + 1);
                          }}
                        />
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Word Cloud Builder Game */}
              {gameStarted && selectedGame === GameId.WORD_CLOUD && (
                <div className="text-center">
                  <div className="text-gray-900 text-lg mb-6">Form positive words from the letters!</div>
                  <div className="flex flex-wrap gap-2 mb-6 justify-center">
                    {wordLetters.map((letter, index) => (
                      <button
                        key={index}
                        className="w-12 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-bold transition-all duration-300 hover:scale-110"
                        onClick={() => setGameScore(prev => prev + 1)}
                      >
                        {letter}
                      </button>
                    ))}
                  </div>
                  <div className="mb-4">
                    <input
                      type="text"
                      className="w-full max-w-md p-4 bg-white border border-orange-200 text-gray-900 rounded-lg text-center focus:ring-2 focus:ring-orange-400 focus:border-orange-400 focus:outline-none"
                      placeholder="Type your positive words here..."
                      onChange={(e) => {
                        if (e.target.value.length > 2) {
                          setFormedWords(prev => [...prev, e.target.value]);
                          setGameScore(prev => prev + 5);
                          e.target.value = '';
                        }
                      }}
                    />
                  </div>
                  <div className="text-gray-700 mb-4">
                    Words formed: {formedWords.length > 0 ? formedWords.join(', ') : 'None yet'}
                  </div>
                  <div className="text-sm text-gray-600">
                    Try words like: FOCUS, CALM, PEACE, ZEN, CHILL
                  </div>
                </div>
              )}

              {/* Game Completed */}
              {gameCompleted && (
                <div className="text-center">
                  <div className="text-6xl mb-4">🎉</div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Game Completed!</h3>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-orange-50/50 rounded-lg p-4 border border-orange-200/50">
                      <div className="text-2xl font-bold text-orange-600">{gameTime}s</div>
                      <div className="text-sm text-gray-600">Time Spent</div>
                    </div>
                    <div className="bg-orange-50/50 rounded-lg p-4 border border-orange-200/50">
                      <div className="text-2xl font-bold text-green-600">{gameScore}</div>
                      <div className="text-sm text-gray-600">Score</div>
                    </div>
                  </div>
                  <div className="text-gray-700 mb-6">
                    Great job! You've completed {getGameById(selectedGame)?.name}!
                  </div>
                </div>
              )}
            </div>

            {/* Footer - Return to Dashboard button (only shown when game is completed) */}
            {gameCompleted && (
              <div className="mt-4 pt-4 border-t border-orange-200/50 flex-shrink-0">
                <button
                  onClick={closeGame}
                  className="w-full px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl font-semibold transition-all duration-300"
                >
                  🏆 Return to Dashboard
                </button>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default GamesSection;

